import { styled } from 'baseui'

export const EditorShell = styled<'div', { $height: number }>('div', ({ $theme, $height }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: `${$height}px`,
  border: `1px solid ${$theme.colors.inputBorder}`,
  borderRadius: $theme.borders.inputBorderRadius,
  backgroundColor: $theme.colors.white,
  position: 'relative',
  ':focus-within': {
    borderColor: $theme.colors.primary,
    boxShadow: `0 0 0 2px ${$theme.colors.bg100}`,
  },
}))

export const ToolbarRow = styled('div', ({ $theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: '2px',
  padding: '4px 6px',
  borderBottom: `1px solid ${$theme.colors.mono100}`,
  backgroundColor: $theme.colors.white,
  borderTopLeftRadius: $theme.borders.inputBorderRadius,
  borderTopRightRadius: $theme.borders.inputBorderRadius,
}))

export const ContextRow = styled('div', ({ $theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: '2px',
  padding: '3px 6px',
  borderBottom: `1px solid ${$theme.colors.mono100}`,
  backgroundColor: $theme.colors.bg050,
  fontSize: '12px',
  color: $theme.colors.mono600,
}))

export const ContextLabel = styled('span', ({ $theme }) => ({
  fontWeight: 600,
  color: $theme.colors.primary,
  marginRight: '6px',
}))

export const ToolbarButton = styled<'button', { $active?: boolean; $disabled?: boolean; $danger?: boolean }>(
  'button',
  ({ $theme, $active, $disabled, $danger }) => ({
    appearance: 'none',
    border: 'none',
    backgroundColor: $active ? $theme.colors.bg100 : 'transparent',
    color: $danger ? $theme.colors.negative : $active ? $theme.colors.primary : $theme.colors.mono700,
    minWidth: '28px',
    height: '30px',
    padding: '0 4px',
    borderRadius: $theme.borders.radius100,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '3px',
    cursor: $disabled ? 'default' : 'pointer',
    opacity: $disabled ? 0.4 : 1,
    fontFamily: 'inherit',
    fontSize: '13px',
    fontWeight: 600,
    lineHeight: 1,
    position: 'relative',
    ':hover': $disabled ? {} : { backgroundColor: $theme.colors.bg050, color: $theme.colors.primary },
    ':focus-visible': { outline: `2px solid ${$theme.colors.primary}`, outlineOffset: '1px' },
  }),
)

export const ToolbarSeparator = styled('span', ({ $theme }) => ({
  width: '1px',
  height: '20px',
  backgroundColor: $theme.colors.mono100,
  margin: '0 2px',
}))

export const SwatchBar = styled<'span', { $color: string }>('span', ({ $color }) => ({
  position: 'absolute',
  left: '6px',
  right: '6px',
  bottom: '4px',
  height: '3px',
  borderRadius: '2px',
  backgroundColor: $color,
}))

export const Menu = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '1px',
  padding: '4px',
  minWidth: '180px',
  maxHeight: '320px',
  overflowY: 'auto',
})

export const MenuItem = styled<'button', { $active?: boolean }>('button', ({ $theme, $active }) => ({
  appearance: 'none',
  border: 'none',
  width: '100%',
  textAlign: 'left',
  backgroundColor: $active ? $theme.colors.bg050 : 'transparent',
  color: $active ? $theme.colors.primary : $theme.colors.textBody,
  padding: '7px 8px',
  borderRadius: $theme.borders.radius100,
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: '14px',
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  ':hover': { backgroundColor: $theme.colors.bg050, color: $theme.colors.primary },
}))

export const MenuHint = styled('span', ({ $theme }) => ({
  marginLeft: 'auto',
  fontSize: '12px',
  color: $theme.colors.mono600,
}))

export const PopoverTitle = styled('div', ({ $theme }) => ({
  fontSize: '12px',
  fontWeight: 600,
  color: $theme.colors.mono600,
  padding: '6px 8px 4px',
}))

export const Swatches = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: '6px',
  padding: '4px 8px',
})

export const Swatch = styled<'button', { $color: string | null; $text?: boolean; $active?: boolean }>(
  'button',
  ({ $theme, $color, $text, $active }) => ({
    width: '34px',
    height: '34px',
    borderRadius: $theme.borders.radius100,
    border: `1px solid ${$theme.colors.mono200}`,
    cursor: 'pointer',
    display: 'grid',
    placeItems: 'center',
    fontWeight: 700,
    fontSize: '15px',
    fontFamily: 'inherit',
    backgroundColor: $text || !$color ? $theme.colors.white : $color,
    color: $text && $color ? $color : $theme.colors.mono600,
    backgroundImage: $color
      ? 'none'
      : `repeating-linear-gradient(45deg, ${$theme.colors.white} 0 6px, ${$theme.colors.mono100} 6px 8px)`,
    outline: $active ? `2px solid ${$theme.colors.primary}` : 'none',
    outlineOffset: '1px',
    ':hover': { outline: `2px solid ${$theme.colors.primary}`, outlineOffset: '1px' },
  }),
)

export const CustomColorRow = styled('div', ({ $theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '8px 8px 4px',
  borderTop: `1px solid ${$theme.colors.mono100}`,
  marginTop: '4px',
}))

export const ColorInput = styled('input', ({ $theme }) => ({
  width: '36px',
  height: '32px',
  padding: '2px',
  border: `1px solid ${$theme.colors.inputBorder}`,
  borderRadius: $theme.borders.radius100,
  backgroundColor: $theme.colors.white,
  cursor: 'pointer',
}))

export const LinkForm = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  padding: '8px',
  width: '360px',
  maxWidth: '90vw',
})

export const LinkActions = styled('div', {
  display: 'flex',
  gap: '6px',
  justifyContent: 'flex-end',
  alignItems: 'center',
})

export const VirtualAnchor = styled<'span', { $top: number; $left: number }>('span', ({ $top, $left }) => ({
  position: 'absolute',
  top: `${$top}px`,
  left: `${$left}px`,
  width: '1px',
  height: '1px',
  pointerEvents: 'none',
}))

export const BubbleBar = styled('div', ({ $theme }) => ({
  display: 'flex',
  gap: '2px',
  padding: '4px',
  backgroundColor: $theme.colors.white,
  border: `1px solid ${$theme.colors.mono100}`,
  borderRadius: $theme.borders.radius200,
  boxShadow: $theme.lighting.shadow600,
}))

export const EmojiPanel = styled('div', {
  width: '352px',
  maxWidth: '90vw',
  padding: '8px',
})

export const EmojiTopRow = styled('div', {
  display: 'flex',
  gap: '6px',
  alignItems: 'center',
})

export const SkinTone = styled<'button', { $color: string; $active: boolean }>(
  'button',
  ({ $theme, $color, $active }) => ({
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    border: `2px solid ${$active ? $theme.colors.primary : 'transparent'}`,
    backgroundColor: $color,
    cursor: 'pointer',
    padding: 0,
  }),
)

export const EmojiCategories = styled('div', {
  display: 'flex',
  gap: '2px',
  margin: '6px 0 4px',
  overflowX: 'auto',
})

export const EmojiGrid = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(9, 1fr)',
  gap: '2px',
  maxHeight: '232px',
  overflowY: 'auto',
})

export const EmojiButton = styled('button', ({ $theme }) => ({
  border: 'none',
  backgroundColor: 'transparent',
  fontSize: '22px',
  lineHeight: 1,
  height: '36px',
  borderRadius: $theme.borders.radius100,
  cursor: 'pointer',
  padding: 0,
  fontFamily: '"Segoe UI Emoji", "Apple Color Emoji", "Noto Color Emoji", sans-serif',
  ':hover': { backgroundColor: $theme.colors.bg050 },
}))

export const EmojiStatus = styled('div', ({ $theme }) => ({
  fontSize: '12px',
  color: $theme.colors.mono600,
  padding: '6px 2px 0',
}))
